import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Check,
  Sparkles,
  Lock,
  Trophy,
  Layers,
  BarChart3,
  LayoutGrid,
  Activity,
  ShieldCheck,
  Eye,
  TrendingUp,
  CalendarClock,
  Ticket,
} from "lucide-react";
import { startCheckout } from "@/lib/billing";
import { toast } from "sonner";
import { useState, useEffect } from "react";
import Disclaimer from "@/components/Disclaimer";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import Seo from "@/components/Seo";

const PRO_BENEFITS = [
  { icon: Trophy, text: "All Top 5 daily plays" },
  { icon: Activity, text: "Full pick breakdowns & AI reasoning" },
  { icon: LayoutGrid, text: "All Picks board across every sport" },
  { icon: Layers, text: "Parlay of the Day (every leg unlocked)" },
  { icon: BarChart3, text: "Full history, units & ROI" },
];

const PRO_FOR = [
  "Full AI reasoning on every play",
  "Every premium pick, every day",
  "Transparent tracked performance",
  "Daily market intelligence",
];

const FALLBACK_STATS = [
  { label: "Tracked record", value: "—" },
  { label: "Tracked profit", value: "—" },
  { label: "Tracked ROI", value: "—" },
  { label: "Cadence", value: "Daily" },
];

const DIFFERENTIATORS = [
  "Every pick tracked publicly in History",
  "AI-assisted analysis with human review",
  "No fake win percentages or hype",
  "Updated daily across major sports",
  "Transparent performance, no hidden archives",
];

export default function Pricing() {
  const { user, isPro, isAdmin } = useAuth();
  const isLoggedOut = !user;
  const canUpgrade = !isPro && !isAdmin;
  const [proLoading, setProLoading] = useState(false);
  const [stats, setStats] = useState(FALLBACK_STATS);

  useEffect(() => {
    (async () => {
      const { data, error } = await supabase.rpc("record_stats", {});
      if (error || !data || !data[0]) return;
      const r = data[0] as { wins: number; losses: number; pushes: number; units: number; roi: number };
      const units = Number(r.units ?? 0);
      const roi = Number(r.roi ?? 0);
      setStats([
        { label: "Tracked record", value: `${r.wins}-${r.losses}-${r.pushes}` },
        { label: "Tracked profit", value: `${units >= 0 ? "+" : ""}${units.toFixed(1)}u` },
        { label: "Tracked ROI", value: `${roi >= 0 ? "+" : ""}${roi.toFixed(1)}%` },
        { label: "Cadence", value: "Daily" },
      ]);
    })();
  }, []);

  const handleProCheckout = async () => {
    if (proLoading) return;
    setProLoading(true);
    try {
      await startCheckout();
    } catch (e: any) {
      toast.error(e?.message ?? "Unable to start checkout");
    } finally {
      setProLoading(false);
    }
  };

  return (
    <div className="container py-12 md:py-16 max-w-6xl">
      <Seo
        title="Pricing — AleaEdge Pro Membership & Daily AI Sports Picks"
        description="Unlock every Top 5 play, full AI breakdowns, parlays and tracked history. Month-to-month, cancel anytime."
        path="/pricing"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          },
          {
            "@context": "https://schema.org",
            "@type": "Product",
            name: "AleaEdge Pro",
            description:
              "Monthly membership unlocking all Top 5 picks, parlays, AI reasoning and tracked history.",
            brand: { "@type": "Brand", name: "AleaEdge" },
            offers: {
              "@type": "Offer",
              price: "19.99",
              priceCurrency: "USD",
              url: "https://aleaedge.com/pricing",
              availability: "https://schema.org/InStock",
            },
          },
        ]}
      />
      {/* HERO */}
      <div className="text-center mb-10 md:mb-12 max-w-3xl mx-auto">
        <span className="te-chip bg-accent/15 text-accent border border-accent/30 mb-4">
          <Sparkles className="w-3 h-3" /> Membership
        </span>
        <h1 className="font-display text-4xl md:text-5xl font-bold leading-tight">
          Unlock every edge. Track every result.
        </h1>
        <p className="text-muted-foreground mt-4 text-base md:text-lg">
          Premium AI sports intelligence — updated daily, tracked transparently.
          Full breakdowns. No gimmicks.
        </p>
      </div>

      {/* TRUST STRIP */}
      <div className="te-card px-4 py-4 md:px-6 md:py-5 mb-10 md:mb-14">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-2">
          {stats.map((s) => (
            <div key={s.label} className="text-center md:border-r md:border-border/50 last:md:border-r-0">
              <div className="font-display text-2xl md:text-3xl font-bold text-foreground">{s.value}</div>
              <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground mt-1">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* PRICING GRID */}
      <div className="grid md:grid-cols-3 gap-5 md:gap-6 items-stretch">
        {/* FREE */}
        <div className="te-card p-7 flex flex-col">
          <div className="flex items-center gap-2 mb-1">
            <Eye className="w-4 h-4 text-muted-foreground" />
            <h3 className="font-display text-2xl font-bold">Free</h3>
          </div>
          <div className="mt-3 flex items-end gap-1">
            <span className="font-display text-5xl font-bold">$0</span>
            <span className="text-muted-foreground mb-1.5">/forever</span>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            A daily taste of the model.
          </p>
          <div className="mt-6 space-y-2.5 flex-1">
            <Item>1 free daily pick</Item>
            <Item>Basic AI summary</Item>
            <Item>Preview today's Top 5 board</Item>
            <Item>See daily featured insights</Item>
            <Item>Public history snapshot</Item>
          </div>
          <Link to="/auth" className="mt-6 block">
            <Button className="w-full" variant="secondary">
              Create free account
            </Button>
          </Link>
          <p className="text-xs text-muted-foreground text-center mt-2">
            No credit card required.
          </p>
        </div>

        {/* PRO */}
        <div className="te-card p-7 ring-2 ring-primary/60 shadow-elevated relative overflow-hidden flex flex-col md:scale-[1.02] md:-my-1">
          <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-gradient-primary opacity-20 blur-3xl pointer-events-none" />
          <div className="flex items-center justify-between gap-2 mb-3">
            <span className="te-chip bg-gradient-primary text-primary-foreground">
              Most popular
            </span>
            <span className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
              Premium access
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary" />
            <h3 className="font-display text-2xl font-bold">Pro</h3>
          </div>
          <div className="mt-3 flex items-end gap-1">
            <span className="font-display text-5xl font-bold">$19.99</span>
            <span className="text-muted-foreground mb-1.5">/mo</span>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Full daily edge — every play, every breakdown.
          </p>

          <div className="mt-6 space-y-2.5">
            {PRO_BENEFITS.map(({ icon: Icon, text }) => (
              <div key={text} className="flex items-start gap-2 text-sm">
                <Icon className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                <span>{text}</span>
              </div>
            ))}
          </div>

          <div className="mt-6 pt-5 border-t border-border/60 flex-1">
            <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground mb-3">
              Built for bettors who want
            </div>
            <ul className="space-y-2">
              {PRO_FOR.map((t) => (
                <li key={t} className="flex items-start gap-2 text-sm">
                  <Check className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>

          {canUpgrade ? (
            <Button
              className="w-full mt-6 bg-gradient-primary text-primary-foreground"
              onClick={handleProCheckout}
              disabled={proLoading}
            >
              <Sparkles className="w-4 h-4 mr-2" />
              {proLoading ? "Starting checkout…" : isLoggedOut ? "Sign up to start Pro" : "Get Full AI Access"}
            </Button>
          ) : (
            <div className="mt-6 rounded-md border border-border/80 bg-secondary/40 px-3 py-2 text-center text-sm text-muted-foreground">
              Pro already active
            </div>
          )}
          <p className="text-xs text-muted-foreground text-center mt-2">
            Cancel anytime · Secure checkout
          </p>
        </div>

        {/* PARLAY ONE-TIME */}
        <div className="te-card p-7 flex flex-col relative overflow-hidden">
          <div className="absolute -bottom-20 -left-16 w-48 h-48 rounded-full bg-accent/10 blur-3xl pointer-events-none" />
          <div className="flex items-center justify-between gap-2 mb-3">
            <span className="te-chip bg-accent/15 text-accent border border-accent/30">
              <Ticket className="w-3 h-3" /> One-time
            </span>
            <span className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
              No subscription
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Layers className="w-4 h-4 text-accent" />
            <h3 className="font-display text-2xl font-bold">Parlay of the Day</h3>
          </div>
          <div className="mt-3 flex items-end gap-1">
            <span className="font-display text-5xl font-bold">$2.99</span>
            <span className="text-muted-foreground mb-1.5">/today</span>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Casual access — unlock just today's premium parlay.
          </p>
          <div className="mt-6 space-y-2.5 flex-1">
            <Item>Today's full parlay revealed</Item>
            <Item>Every leg, line & reasoning</Item>
            <Item>Tracked publicly in History</Item>
            <Item>One-time purchase, no renewal</Item>
          </div>
          {canUpgrade ? (
            <Link to={isLoggedOut ? "/auth" : "/dashboard"} className="mt-6 block">
              <Button className="w-full" variant="outline">
                <Ticket className="w-4 h-4 mr-2" />
                {isLoggedOut ? "Sign up to unlock parlay" : "Unlock today's parlay"}
              </Button>
            </Link>
          ) : (
            <div className="mt-6 rounded-md border border-border/80 bg-secondary/40 px-3 py-2 text-center text-sm text-muted-foreground">
              Included with your access
            </div>
          )}
          <p className="text-xs text-muted-foreground text-center mt-2">
            Available daily on the Dashboard.
          </p>
        </div>
      </div>

      {/* EDITORIAL TRUST LINE */}
      <div className="mt-10 md:mt-12 te-card p-5 md:p-6 flex items-start md:items-center gap-3 md:gap-4">
        <ShieldCheck className="w-5 h-5 text-primary mt-0.5 md:mt-0 shrink-0" />
        <p className="text-sm md:text-[15px] text-muted-foreground">
          Every published play remains permanently tracked in our public{" "}
          <Link to="/history" className="text-foreground underline decoration-border underline-offset-4 hover:text-primary">
            History
          </Link>
          . No deleted losses. No selective wins. Just the record.
        </p>
      </div>

      {/* DIFFERENTIATORS */}
      <div className="mt-14 md:mt-16">
        <div className="text-center max-w-2xl mx-auto mb-8">
          <h2 className="font-display text-2xl md:text-3xl font-bold">
            What makes AleaEdge different
          </h2>
          <p className="text-muted-foreground mt-2">
            Built around transparency, not hype.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {DIFFERENTIATORS.map((d, i) => (
            <div key={d} className="te-card p-5 flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
                {i === 0 ? <Eye className="w-4 h-4 text-primary" /> :
                 i === 1 ? <Sparkles className="w-4 h-4 text-primary" /> :
                 i === 2 ? <ShieldCheck className="w-4 h-4 text-primary" /> :
                 i === 3 ? <CalendarClock className="w-4 h-4 text-primary" /> :
                           <TrendingUp className="w-4 h-4 text-primary" />}
              </div>
              <span className="text-sm leading-relaxed">{d}</span>
            </div>
          ))}
        </div>
      </div>

      {/* FAQ */}
      <div className="mt-14 md:mt-16">
        <div className="text-center max-w-2xl mx-auto mb-8">
          <h2 className="font-display text-2xl md:text-3xl font-bold">
            Frequently asked
          </h2>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {FAQ.map((f) => (
            <div key={f.q} className="te-card p-5">
              <div className="font-display font-bold">{f.q}</div>
              <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-12">
        <Disclaimer />
      </div>
    </div>
  );
}

function Item({ children, muted }: { children: React.ReactNode; muted?: boolean }) {
  return (
    <div className={`flex items-start gap-2 text-sm ${muted ? "text-muted-foreground" : ""}`}>
      {muted ? (
        <Lock className="w-4 h-4 text-muted-foreground/70 mt-0.5 shrink-0" />
      ) : (
        <Check className="w-4 h-4 text-primary mt-0.5 shrink-0" />
      )}
      <span>{children}</span>
    </div>
  );
}

const FAQ = [
  { q: "How is Edge % calculated?", a: "Edge % compares our projected probability against the sportsbook's implied probability on the best available price across major US books. Consensus probability is averaged across multiple books before we measure the gap — a real, market-anchored number." },
  { q: "Why are AleaEdge edges lower than other betting apps?", a: "Most apps inflate edge to look impressive in marketing. AleaEdge uses conservative modeling tied to real sportsbook pricing discrepancies. Small honest edges compound over time — fake double-digit edges do not." },
  { q: "What makes AleaEdge different?", a: "Transparent AI-assisted sportsbook analysis, conservative probability modeling, real market discrepancies and a fully tracked public history. No hype, no fairy tales — just disciplined betting intelligence." },
  { q: "Why does AleaEdge use conservative modeling?", a: "Because we're built for long-term bettors. Conservative edge calculations protect users from over-sizing plays based on inflated EV. We'd rather underpromise and let the public track record speak." },
  { q: "How often are picks updated?", a: "New plays are published daily across the major sports we cover, with the Top 5 board and Parlay of the Day refreshed every day." },
  { q: "Are picks AI-generated?", a: "Yes — our 100-point engine grades each play across 7 signals. Every published pick is then reviewed by an admin before going live." },
  { q: "Is performance tracked publicly?", a: "Always. Every published pick lives permanently in our public History — wins, losses, and pushes." },
  { q: "Can I cancel anytime?", a: "Yes. Pro is month-to-month with no contracts or hidden fees. Cancel from your account in one click." },
  { q: "How does Parlay of the Day work?", a: "It's a one-time $2.99 unlock for today's premium parlay only. No subscription, no renewal — perfect for casual access." },
  { q: "What sports are covered?", a: "We rotate coverage across major US and international sports based on the daily slate, focusing on where the model finds edge." },
  { q: "Do you guarantee wins?", a: "No one can. Picks are research and entertainment — never a guarantee. Always bet responsibly." },
  { q: "What sportsbooks are supported?", a: "Lines reference major US sportsbooks. You're free to shop your own books for the best available number on each play." },
];
